import React from 'react';
import { Star, ShieldCheck, ShoppingBag, Truck, Edit, Trash2 } from 'lucide-react';

import { Supplier } from '../helpers/suppliersHelpers';
export type { Supplier };

interface SupplierCardProps {
  supplier: Supplier;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  onClick: () => void;
}

const SupplierCard: React.FC<SupplierCardProps> = ({
  supplier,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onClick,
}) => {
  return (
    <div
      onClick={onClick}
      className="group relative flex flex-col justify-between overflow-hidden bg-[#1a1d21]/60 hover:bg-[#1a1d21] border border-gray-800 hover:border-[#0FFF55]/50 rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-[#0FFF55]/5 cursor-pointer h-full"
    >
      {/* Admin / Supplier Actions overlay */}
      {(canEdit || canDelete) && (
        <div className="absolute top-3 right-3 z-20 flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
          {canEdit && (
            <button
              onClick={onEdit}
              className="p-2 bg-gray-900/80 hover:bg-[#0FFF55] text-gray-300 hover:text-black rounded-lg border border-gray-800 hover:border-[#0FFF55] transition-all"
              title="Editar Fornecedor"
            >
              <Edit size={14} />
            </button>
          )}
          {canDelete && (
            <button
              onClick={onDelete}
              className="p-2 bg-gray-900/80 hover:bg-red-600 text-gray-300 hover:text-white rounded-lg border border-gray-800 hover:border-red-600 transition-all"
              title="Excluir Fornecedor"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      )}

      {/* Main Image Header */}
      <div>
        <div className="relative h-32 bg-gradient-to-r from-gray-900 to-gray-800 rounded-t-2xl">
          {/* Image and Gradient Wrapper with overflow-hidden */}
          <div className="absolute inset-0 overflow-hidden rounded-t-2xl">
            {supplier.bannerUrl && (
              <img
                src={supplier.bannerUrl}
                alt={`${supplier.name} Banner`}
                className="w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-500"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1d21] to-transparent" />
          </div>
          
          {/* Logo container - stays unclipped outside the image overflow-hidden wrapper */}
          <div className="absolute -bottom-6 left-6 w-16 h-16 bg-[#121316] border-2 border-gray-800 rounded-xl overflow-hidden shadow-lg p-1 z-10">
            <img
              src={supplier.logoUrl || 'https://images.unsplash.com/photo-1572021335469-31706a17aaef?w=100&h=100&fit=crop'}
              alt={supplier.name}
              className="w-full h-full object-contain rounded-lg"
              onError={(e) => {
                e.currentTarget.src = 'https://images.unsplash.com/photo-1572021335469-31706a17aaef?w=100&h=100&fit=crop';
              }}
            />
          </div>
        </div>

        {/* Card Body */}
        <div className="pt-8 px-6 pb-4">
          <div className="flex items-center space-x-2 mb-2">
            <h3 className="text-white font-bold text-lg leading-tight group-hover:text-[#0FFF55] transition-colors">
              {supplier.name}
            </h3>
            {supplier.isVerified && (
              <ShieldCheck className="w-5 h-5 text-[#0FFF55] fill-[#0FFF55]/10 flex-shrink-0" title="Verificado" />
            )}
          </div>

          {/* Rating */}
          <div className="flex items-center space-x-1.5 mb-3">
            <div className="flex items-center text-yellow-500">
              <Star className="w-4 h-4 fill-yellow-500" />
            </div>
            <span className="text-sm font-semibold text-gray-200">{supplier.rating.toFixed(1)}</span>
            <span className="text-xs text-gray-500">/ 5.0</span>
          </div>

          <p className="text-gray-400 text-xs leading-relaxed line-clamp-2 min-h-[2rem]">
            {supplier.descriptionShort}
          </p>

          {/* Categories list */}
          {supplier.categories && supplier.categories.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
              {supplier.categories.slice(0, 2).map((cat) => (
                <span
                  key={cat}
                  className="px-2 py-0.5 bg-[#0FFF55]/5 border border-[#0FFF55]/15 text-[#0FFF55] rounded-full text-[9px] font-medium"
                >
                  {cat}
                </span>
              ))}
              {supplier.categories.length > 2 && (
                <span className="px-2 py-0.5 bg-gray-900 border border-gray-800 text-gray-400 rounded-full text-[9px] font-medium">
                  +{supplier.categories.length - 2}
                </span>
              )}
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-800/80">
            <div className="flex items-center space-x-2 text-gray-300">
              <Truck size={14} className="text-[#0FFF55]" />
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Envios</p>
                <p className="text-xs font-bold text-white">{supplier.enviosCount.toLocaleString()}+</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-gray-300">
              <ShoppingBag size={14} className="text-[#0FFF55]" />
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Produtos</p>
                <p className="text-xs font-bold text-white">{supplier.produtosCount.toLocaleString()}+</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Footer */}
      <div className="px-6 pb-6 pt-2 flex gap-3">
        {supplier.whatsapp && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              window.open(supplier.whatsapp, '_blank');
            }}
            className="w-1/2 py-2.5 px-3 bg-[#121316] hover:bg-[#25D366] text-gray-300 hover:text-white font-semibold text-xs rounded-xl border border-gray-800 hover:border-[#25D366] transition-all flex items-center justify-center space-x-1.5"
          >
            <span>WhatsApp</span>
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            window.open(supplier.redirectUrl || 'https://mastershop.com', '_blank');
          }}
          className={`${supplier.whatsapp ? 'w-1/2' : 'w-full'} py-2.5 px-3 bg-[#121316] hover:bg-[#0FFF55] text-gray-300 hover:text-black font-semibold text-xs rounded-xl border border-gray-800 hover:border-[#0FFF55] transition-all flex items-center justify-center space-x-1.5`}
        >
          <span>Ir para Mastershop</span>
        </button>
      </div>
    </div>
  );
};

export default SupplierCard;
